import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Container, Row, Col } from "react-bootstrap";
import TableFilter from "../../../components/TableFilter";
import { useQuery } from "@tanstack/react-query";
import CenterLoadingContainer from "../../../components/CenterLoadingContainer";

const fetchCompanyData = async (token) => {
  const url =
    MOBY_API_URL +
    "/api/usermanager/v1/users/" +
    token?.user?.sub +
    "/companies?txid=" + token.sessionId;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token.token,
    },
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const fetchPanelsList = async (token, companyId) => {
  const response = await fetch(
    MOBY_API_URL + "/api/companymanager/v1/companies/" + companyId + "/devices?txid=" + token.sessionId,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token.token,
      },
    }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const PanelsList = ({ onPanelSelect, token }) => {
  const columns = useMemo(
    () => [
      { accessorKey: "cloudId", header: "Cloud ID" },
      { accessorKey: "panelName", header: "Panel Name" },
    ],
    []
  );
  const {
    data: companyData,
    isLoading: fetchCompanyLoading,
    isError: fetchCompanyError,
  } = useQuery({
    queryKey: ["companyData"],
    queryFn: () => fetchCompanyData(token),
    enabled: !!token?.user?.sub,
  });

  const companyId = useMemo(() => {
    if (companyData?.data?.length > 0) {
      return companyData.data[0]?.companyId || null;
    }
    return null;
  }, [companyData]);

  const {
    data: panelsList,
    isLoading: fetchListLoading,
    isError: fetchListError,
    isFetching,
  } = useQuery({
    queryKey: ["panelsList", companyId],
    queryFn: () => fetchPanelsList(token, companyId),
    enabled: !!companyId,
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});

  const duplicateCloudIds = useMemo(
    () => new Set(panelsList?.data?.duplicateCloudIds || []),
    [panelsList]
  );

  const table = useReactTable({
    data: panelsList?.data?.panels || [],
    columns,
    state: {
      globalFilter,
      rowSelection,
    },
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "auto",
    enableRowSelection: true,
    enableMultiRowSelection: false,
    getRowId: (row) => row.id,
  });

  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length > 0) {
      onPanelSelect(selectedRows[0].original);
    } else {
      onPanelSelect(null);
    }
  }, [rowSelection, onPanelSelect, table]);

  if (fetchCompanyLoading || fetchListLoading || isFetching) {
    return <CenterLoadingContainer />;
  }

  if (fetchCompanyError) return <div>Couldn't find company data</div>;
  if (fetchListError) return <div>Couldn't find panels list</div>;

  return (
    <Container>
      <Row className="mt-4">
        <Col>
          <div className="mb-2">
            <TableFilter
              filterValue={globalFilter}
              setFilter={setGlobalFilter}
            />
          </div>

          <Table bordered variant="info" className="fixed-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                const isDuplicate = duplicateCloudIds.has(row?.original?.cloudId);
                return (
                <tr
                  key={row.id}
                  className={isDuplicate ? "panel-row-duplicate no-select" : row.getIsSelected() ? "panel-row-selected" : "panel-row-normal"}
                  onClick={() => { if (!isDuplicate) row.toggleSelected(); }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{ backgroundColor: "inherit", cursor: "pointer" }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );})}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default PanelsList;
